import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { UserProfileDto, UserRole } from "@green0meter/api-client";

import { DOMAIN_EMISSION_FACTORS } from "../domain";

import { emissionFactorsApi } from "@api/services";
import { ACTION_GET_EMISSION_FACTORS, roleGuard } from "@api";

export const ACTION_UPDATE_EMISSION_FACTOR = "UPDATE_EMISSION_FACTOR";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type UpdateEmissionFactorPairDto = {
  emissionFactorId: string;
  questionCode: string;
  currentPairId?: string | undefined;
};

const updateEmissionFactorPair = async (
  dto: UpdateEmissionFactorPairDto,
): Promise<void> => {
  await emissionFactorsApi.emissionFactorsControllerUpdateEmissionFactorPair(
    dto.emissionFactorId,
    {
      questionCode: dto.questionCode,
      currentPairId: dto.currentPairId,
    },
  );
};

export const useUpdateEmissionFactorPair = (
  user: UserProfileDto | null,
): {
  updateEmissionFactorPair: UseMutateFunction<
    void,
    unknown,
    UpdateEmissionFactorPairDto,
    unknown
  >;
  updateEmissionFactorPairAsync: (dto: UpdateEmissionFactorPairDto) => Promise<void>;
  updateEmissionFactorPairEnabled: boolean;
  isSuccessUpdateEmissionFactorPair: boolean;
  isPendingUpdateEmissionFactorPair: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_EMISSION_FACTORS, ACTION_UPDATE_EMISSION_FACTOR],
    mutationFn: enabled ? updateEmissionFactorPair : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_EMISSION_FACTORS, ACTION_GET_EMISSION_FACTORS],
      });
    },
  });

  return {
    updateEmissionFactorPair: mutate,
    updateEmissionFactorPairAsync: mutateAsync,
    updateEmissionFactorPairEnabled: enabled,
    isSuccessUpdateEmissionFactorPair: isSuccess,
    isPendingUpdateEmissionFactorPair: isPending,
  };
};
